import { useState, useRef, useEffect } from "react";
import Modal from "../shared/Modal";
import { useSelector, useDispatch } from "react-redux";
import {
  apiCreateWorkspaceForUser,
  apiGetWorkspace,
  apiSetCurrentWorkspace,
} from "../../api.js";
import {
  selectUser,
  setUser,
  setLinkRecords,
  setUIStates,
  selectUIStates,
  TOOLS,
  setEditingCollection,
  setSelectedMenu,
  setSelectedMenuId,
} from "../../redux/features/appData";
import {
  clearRecentSelections,
  setMessages,
  setCurrentThread,
  setPrompt,
  setInitialLoading,
  setEmployees,
  setPersonas,
} from "../../redux/features/agent/agentSlice";
import { MENUS } from "../config/menus";
import { TRACK_EVENTS, trackAnalyticsEvent } from "../../hooks/useAnalytics";
import "./ModalAddWorkspace.css";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { apiCreateCollectionWithEverything } from "../../services/CollectionService";
import {
  CollectionAccessTypes,
  CreateCollectionWithEverythingContract,
} from "winslow-core";
import { toastFail } from "../../config";
import toast from "react-hot-toast";
import { Button } from "../../ds2/components";
import config from "environment";

type ModalAddWorkspaceProps = {
  isOpen: boolean;
  onClose: any;
  onSuccess: any;
};

export default function ModalAddWorkspace({
  isOpen = false,
  onClose,
  onSuccess,
}: ModalAddWorkspaceProps) {
  const uiStates = useSelector(selectUIStates);

  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader();
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newWorkspaceError, setNewWorkspaceError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const storeUser = useSelector(selectUser);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const NOT_PROD = config.environment !== "production";

  const saveNewWorkspace = async () => {
    if (!storeUser) {
      return;
    }

    // Strip ?data= parameter if in production
    let workspaceName = newWorkspaceName;
    if (!NOT_PROD) {
      // Remove ?data= parameter in production
      workspaceName = newWorkspaceName.replace(/\?data=[^&]+.*$/, "").trim();
    }

    const workspace = await apiCreateWorkspaceForUser(
      workspaceName,
      storeUser.id
    );

    if (workspace) {
      // now create the default collection
      const payload = {
        title: "title for workspace " + workspace.id,
        description: "",
        color: "#B9B9B9",
        userId: storeUser?.id,
        workspaceId: workspace.id,
        accessType: CollectionAccessTypes.TEAM,
      } as CreateCollectionWithEverythingContract["Body"];

      const collection = await apiCreateCollectionWithEverything(payload);

      if (!collection) {
        toast("Problem creating collection for workspace.", toastFail);
        return;
      }

      dispatch(setEditingCollection(collection));

      const updatedWorkspace = await apiGetWorkspace(workspace.id);

      trackAnalyticsEvent(TRACK_EVENTS.CreateWorkspace, {}, storeUser);

      // add the new one to the current store user
      const newWorkspaces = [...storeUser.workspaces, updatedWorkspace];
      const newUser = {
        ...storeUser,
        workspaces: newWorkspaces,
        currentWorkspaceId: updatedWorkspace.id,
      };

      dispatch(setUser(newUser));
      dispatch(setSelectedMenu(MENUS.HOME));
      dispatch(setSelectedMenuId(undefined));

      // Clear agent state before loading workspace data
      dispatch(clearRecentSelections());
      dispatch(setEmployees([]));
      dispatch(setPersonas([]));
      dispatch(setMessages([]));
      dispatch(setCurrentThread(null));
      dispatch(setPrompt(""));
      dispatch(setInitialLoading(true));

      dispatch(setLinkRecords(null));

      // Reload all data for the new workspace and capture the collection ID
      const newCollectionId = await reloadAll(updatedWorkspace.id);
      await apiSetCurrentWorkspace(storeUser.id, workspace.id);

      dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.CHAT }));
      onSuccess(newCollectionId || collection.id);
    } else {
      dispatch(setUIStates({ ...uiStates, selectedTool: TOOLS.CHAT }));
      onSuccess();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Modal
        onClose={onClose}
        style={{}}
        isOpen={isOpen}
        canClose={false}
        allowClickout={true}>
        <div className="add-workspace-container">
          Create A New Workspace
          <div className="add-workspace-input">
            <input
              ref={inputRef}
              type="text"
              autoFocus={true}
              placeholder="Name your workspace"
              value={newWorkspaceName}
              onChange={(e) => {
                setNewWorkspaceName(e.target.value);
              }}
              onKeyDown={(e: any) => {
                if (e.key === "Enter" && newWorkspaceName.length > 0) {
                  saveNewWorkspace();
                }
              }}
            />
            {newWorkspaceError && (
              <div style={{ color: "var(--text-color-error)" }}>
                {newWorkspaceError}
              </div>
            )}
          </div>
          <div>
            You can change your name and customize your workspace logo at
            anytime in your workspace settings.
          </div>
          <div className="add-workspace-buttons">
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={newWorkspaceName?.length < 1}
              onClick={saveNewWorkspace}
              variant="primary">
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
